import React, { createContext, useContext, useEffect, useState } from "react";
import {
  browserLocalPersistence,
  EmailAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  setPersistence,
  updatePassword,
} from "firebase/auth";
import { auth } from "@firebase";
import { UserService } from "services/UserService";
import { CartService } from "services/CartService";

interface AuthContextType {
  currentUser: any;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  isLoggedIn: boolean;
  setCurrentUser: (user: any) => void;
  handleUpdateCartItem: (itemId: string, quantity: number) => Promise<void>;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  isPending: boolean;
  updatePendingStatus: () => void; // ✅ Add function type
  isQuantityUpdate?: number;
  updateUserPassword: (
    newPassword: string
  ) => Promise<{ success: boolean; message: string }>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  setLoading: () => {},
  isLoggedIn: false,
  setCurrentUser: () => {},
  handleUpdateCartItem: async () => {},
  searchTerm: "",
  setSearchTerm: () => {},
  isPending: false, // ✅ Default value
  updatePendingStatus: () => {}, // ✅ Default value
  updateUserPassword: async () => ({
    success: false,
    message: "Not implemented",
  }), // Placeholder
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContextProvider");
  }
  return context;
};

// ✅ Function to check if all steps are completed
const checkAllStepsCompleted = (userInfo: any) => {
  if (!userInfo) return false;

  return (
    !!userInfo.biography?.trim() &&
    !!userInfo.degrees?.length &&
    !!userInfo.certifications?.length &&
    !!userInfo.licenseNumber?.trim() &&
    !!userInfo.insuranceNumber?.trim() &&
    !!userInfo.twitter?.trim()
  );
};

export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>(""); // Add search term state
  const [isPending, setIsPending] = useState(false); // ✅ Track pending status
  const [isQuantityUpdate, setIsQuantityUpdate] = useState(1);

  useEffect(() => {
    // ✅ Set Firebase session persistence
    // setPersistence(auth, browserLocalPersistence)

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      if (user) {
        const email = user.email;
        const uid = user.uid;

        if (!email || !uid) {
          console.error("User email or UID is missing.");
          return;
        }

        try {
          // Fetch userInfo by UID instead of email
          const userInfo = await UserService.getUserByid(uid);
          if (!userInfo) {
            console.error("Invalid credentials or user not found.");
            return;
          }

          const userWithInfo = { ...user, userInfo };

          setCurrentUser(userWithInfo);

          // ✅ Check if the user has completed all steps
          setIsPending(checkAllStepsCompleted(userInfo)); // ✅ Immediately update `isPending`
        } catch (error) {
          console.error("Error fetching or creating user document:", error);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isPending]);

  // ✅ Function to update `isPending` when user completes the fields
  const updatePendingStatus = () => {
    if (currentUser?.userInfo) {
      setIsPending(!checkAllStepsCompleted(currentUser.userInfo));
    }
  };

  const handleUpdateCartItem = async (itemId: string, quantity: number) => {
    setLoading(true);
    try {
      CartService.updateCartItemQuantity(itemId, quantity).then(() => {
        setIsQuantityUpdate(quantity);
      });
    } catch (error) {
      console.error("Error updating cart item quantity:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Function to update password globally
  const updateUserPassword = async (newPassword: string) => {
    const user = auth.currentUser; // ✅ Ensure valid Firebase user
    if (!user) {
      return { success: false, message: "User not authenticated." };
    }

    try {
      await updatePassword(user, newPassword);
      return { success: true, message: "Password updated successfully!" };
    } catch (error: any) {
      if (error.code === "auth/requires-recent-login") {
        try {
          // ✅ Prompt re-authentication
          const credential = EmailAuthProvider.credential(
            user.email!,
            newPassword
          );
          await reauthenticateWithCredential(user, credential);
          await updatePassword(user, newPassword);
          return {
            success: true,
            message: "Password updated successfully after re-authentication!",
          };
        } catch (reauthError: any) {
          return {
            success: false,
            message: "Re-authentication failed. Please log in again.",
          };
        }
      }
      return { success: false, message: error.message };
    }
  };

  const isLoggedIn = !!currentUser;

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        loading,
        setLoading,
        isLoggedIn,
        setCurrentUser,
        handleUpdateCartItem,
        searchTerm, // Provide searchTerm
        setSearchTerm, // Provide setSearchTerm function
        isPending,
        isQuantityUpdate,
        updatePendingStatus, // ✅ Provide updatePendingStatus function
        updateUserPassword, // ✅ Provide function globally
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
